import { useMemo } from 'react';

import { useWorkspace } from '../../contexts/WorkspaceContext';

/**
 * Whether the active workspace has PHI (Protected Health Information)
 * mode enabled. PHI workspaces are subject to HIPAA controls — most
 * notably a 15-minute idle timeout and a strict "no third-party
 * processors without a signed BAA" posture.
 *
 * Connectors are gated by this hook: PHI workspaces lose the
 * connectors UI because the gateway provider's BAA isn't signed yet.
 * The tab and the picker simply aren't there. The backend also
 * refuses connection writes from PHI workspaces, so this is
 * defence-in-depth rather than the primary gate.
 *
 * Returns ``false`` until the workspace context loads, so on its own it
 * cannot tell "not PHI" from "not known yet". A control that must never
 * show in a PHI workspace also waits for ``workspace`` to be non-null, as
 * ``ConnectorPickerButton`` does.
 */
export function useIsPhiWorkspace(): boolean {
  const { workspace } = useWorkspace();
  return useMemo(() => {
    const settings = (workspace as Record<string, unknown> | null)?.settings as
      | Record<string, unknown>
      | undefined;
    return settings?.phi === true;
  }, [workspace]);
}
